/**
 * useMySubscriptions — SOLE network owner for GET subscriptions/mine.
 *
 * Module-scope singleton: every caller shares the same reactive `rows` ref.
 * `fetchMySubscriptions()` is the single network call for the union list
 * (all franchises the caller can access, live + terminal history).
 *
 * No filter scoping (useFiltro is intentionally NOT used: the endpoint is
 * user-scoped, not filter-scoped — filtering would hide lapsed franchises
 * the user needs to see and act on).
 *
 * All network lives here (no_httpservice_in_vue rule).
 * Call `_reset()` in afterEach for test isolation.
 */
import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_SubscriptionRow } from '../contracts/Subscriptions_SubscriptionRow'
import type { Subscriptions_MySubscriptionsResponseDados } from '../contracts/Subscriptions_MySubscriptionsResponse'

// Side-effect import: registers the contract with the responseContract registry.
import '../contracts/Subscriptions_MySubscriptionsResponse'
import '../contracts/Subscriptions_SubscriptionRow'

// ── Module-scope singleton state ────────────────────────────────────────────

const rows = ref<Subscriptions_SubscriptionRow[]>([])

// ── Public composable ────────────────────────────────────────────────────────

export function useMySubscriptions() {
  /**
   * fetchMySubscriptions — GET subscriptions/mine.
   *
   * Unwraps resp.dados.rows ?? [] into the module-scope `rows` ref.
   * On error: logs and re-throws (no silent catch — the caller sees the error).
   */
  async function fetchMySubscriptions(): Promise<void> {
    try {
      const raw = await httpService.get(
        `${config.api.sistemaHost}/subscriptions/mine`,
        { responseContract: 'Subscriptions_MySubscriptionsResponse' },
      )
      const resp = raw as { dados: Subscriptions_MySubscriptionsResponseDados }
      rows.value = resp.dados?.rows ?? []
      log('useMySubscriptions', 'fetchMySubscriptions', 'Assinaturas carregadas', { count: rows.value.length })
    } catch (err) {
      log('useMySubscriptions', 'fetchMySubscriptions', 'Erro ao carregar assinaturas', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    rows.value = []
  }

  return {
    rows,
    fetchMySubscriptions,
    _reset,
  }
}
